/**
 * iter168 — Per-file gallery upload queue.
 *
 * Renders a modal-style list of every selected file with an individual
 * live status: Waiting → Uploading → (Uploaded | Duplicate | Failed).
 * Uploads sequentially, one file per POST, so each request has its own
 * `onUploadProgress` byte counter and can be retried independently.
 *
 * Design constraints deliberately honoured:
 *   • The backend `POST /gallery/events/{id}/photos` already accepts a
 *     single-file batch, so this component does NOT change the server.
 *   • AI face indexing is untouched — the backend still runs its own
 *     inline embed / background embed pipeline on successful upload.
 *   • Duplicate detection remains the server's job — this component only
 *     translates the server's `errors[0].error == "Duplicate ..."` into
 *     a distinct visual state.
 *   • Large batches: rows are `React.memo`'d and keyed by an immutable
 *     item id so a status flip on file #372 does NOT rerender the other
 *     rows.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircle2,
  Circle,
  Loader2,
  RefreshCw,
  AlertTriangle,
  X,
  Copy,
} from "lucide-react";
import api from "@/lib/api";
import { compressImage, humanBytes } from "@/lib/imageCompress";
import { formatApiError } from "@/lib/utils";

const MAX_AUTO_RETRIES = 2;
// iter169 — Run up to N uploads concurrently. Each worker owns one slot;
// when the file it's on finishes (uploaded / duplicate / failed after
// retries), the worker pulls the next id off `queueRef` until drained.
const MAX_CONCURRENT_UPLOADS = 5;
const RETRY_BACKOFF_MS = 400;

// Immutable helper — keep the queue update logic pure so React batches
// don't stomp on each other during high-throughput status flips.
function patchItem(list, id, patch) {
  return list.map((it) => (it.id === id ? { ...it, ...patch } : it));
}

function classifyErrorText(text) {
  if (!text) return "failed";
  return /duplicate/i.test(text) ? "duplicate" : "failed";
}

// iter169 — Small client-side dedup pass so a batch that contains the same
// file selected 3× doesn't race the server's non-unique sha256 index and
// produce two "Uploaded" rows for the same content. Cross-batch dedup is
// still the server's job — we only defend within a single batch here.
async function sha256Hex(file) {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null; // If SubtleCrypto is unavailable we just skip the pre-check.
  }
}

const STATUS_META = {
  waiting: { label: "Waiting", tone: "text-stone-500" },
  compressing: { label: "Optimising…", tone: "text-stone-600" },
  uploading: { label: "Uploading", tone: "text-sky-700" },
  retrying: { label: "Retrying", tone: "text-amber-700" },
  uploaded: { label: "Uploaded", tone: "text-emerald-700" },
  duplicate: { label: "Duplicate · Skipped", tone: "text-amber-700" },
  failed: { label: "Failed", tone: "text-rose-700" },
};

const StatusIcon = React.memo(function StatusIcon({ status }) {
  if (status === "uploaded")
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />;
  if (status === "duplicate")
    return <Copy className="w-4 h-4 text-amber-600" strokeWidth={2} />;
  if (status === "failed")
    return <AlertTriangle className="w-4 h-4 text-rose-600" strokeWidth={2} />;
  if (status === "uploading" || status === "retrying" || status === "compressing")
    return <Loader2 className="w-4 h-4 text-sky-600 animate-spin" strokeWidth={2} />;
  return <Circle className="w-4 h-4 text-stone-300" strokeWidth={2} />;
});

const QueueRow = React.memo(function QueueRow({ item, onRetry }) {
  const meta = STATUS_META[item.status] || STATUS_META.waiting;
  const showBar = item.status === "uploading" || item.status === "retrying";
  return (
    <li
      className="flex items-center gap-3 px-3 py-2 border-b border-stone-100 last:border-b-0"
      data-testid={`upload-row-${item.id}`}
    >
      <StatusIcon status={item.status} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-stone-800 truncate" title={item.name}>
          {item.name}
        </div>
        <div className={`text-[11px] ${meta.tone} flex items-center gap-2`}>
          <span data-testid={`upload-row-status-${item.id}`}>
            {item.status === "retrying"
              ? `Retrying ${item.retries}/${MAX_AUTO_RETRIES}…`
              : meta.label}
            {item.status === "uploading" && item.progress > 0
              ? ` · ${item.progress}%`
              : ""}
          </span>
          {item.status === "failed" && item.error ? (
            <span className="text-rose-600 truncate">— {item.error}</span>
          ) : null}
          <span className="text-stone-400 ml-auto tabular-nums">{humanBytes(item.size)}</span>
        </div>
        {showBar && (
          <div className="w-full bg-stone-100 rounded-full h-1 overflow-hidden mt-1">
            <div
              className="h-full bg-sky-500 transition-all"
              style={{ width: `${item.progress || 0}%` }}
            />
          </div>
        )}
      </div>
      {item.status === "failed" && (
        <button
          type="button"
          className="btn btn-outline btn-sm !py-0.5 !text-[11px]"
          onClick={() => onRetry(item.id)}
          data-testid={`upload-row-retry-${item.id}`}
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      )}
    </li>
  );
});

export default function GalleryUploadQueue({
  open,
  onClose,
  files,           // File[] the parent handed in for this batch
  eventId,
  uploadPreset,    // "original" | "balanced" | "fast"
  onBatchDone,     // called once when the whole queue drains — parent refreshes photos
}) {
  const [items, setItems] = useState([]);        // queue rows
  const [busy, setBusy] = useState(false);       // is any file currently in-flight?
  const queueRef = useRef([]);                   // FIFO of item ids still to process
  const itemsRef = useRef([]);                   // latest items snapshot for the pump

  // ---- Seed the queue whenever the parent hands us a fresh file batch ----
  useEffect(() => {
    if (!open || !files || files.length === 0) return;
    let cancelled = false;
    (async () => {
      const seeded = files.map((f, idx) => ({
        id: `${Date.now()}-${idx}-${f.name}`,
        name: f.name,
        size: f.size,
        file: f,
        status: "waiting",
        progress: 0,
        error: "",
        retries: 0,
      }));
      // Client-side intra-batch dedup. Hash each file once; any file whose
      // hash matches an earlier one in the SAME batch is marked duplicate
      // without ever hitting the server (avoids the sha256-race under
      // concurrency). Files with unreadable hashes are still queued.
      const seenHash = new Map(); // hash → id of the first file that owned it
      for (const it of seeded) {
        const h = await sha256Hex(it.file);
        if (cancelled) return;
        if (!h) continue;
        if (seenHash.has(h)) {
          it.status = "duplicate";
          it.progress = 100;
        } else {
          seenHash.set(h, it.id);
        }
      }
      if (cancelled) return;
      setItems(seeded);
      itemsRef.current = seeded;
      queueRef.current = seeded
        .filter((s) => s.status !== "duplicate")
        .map((s) => s.id);
      if (queueRef.current.length === 0) {
        // Nothing to upload — the whole batch was pre-detected as duplicate.
        onBatchDone?.();
        return;
      }
      pumpQueue();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, files]);

  const updateItem = useCallback((id, patch) => {
    // Keep the ref in sync so the pump loop always sees the latest snapshot
    // without waiting for React to commit.
    itemsRef.current = itemsRef.current.map((it) =>
      it.id === id ? { ...it, ...patch } : it,
    );
    setItems(itemsRef.current);
  }, []);

  // Actually POSTs a single file. Returns an outcome object so the pump
  // knows whether to auto-retry (`retryable`) or stop trying (permanent).
  //   uploaded  → server accepted the file
  //   duplicate → server refused because of sha256 clash (never retry)
  //   failed    → transient / permanent error; caller decides via `retryable`
  const uploadOne = useCallback(async (item) => {
    let fileToSend = item.file;
    if (uploadPreset !== "original") {
      updateItem(item.id, { status: "compressing" });
      try {
        fileToSend = await compressImage(item.file, uploadPreset);
      } catch {
        // Best-effort — fall back to the original file. Server may still accept it.
        fileToSend = item.file;
      }
    }
    updateItem(item.id, { status: item.retries > 0 ? "retrying" : "uploading", progress: 0 });

    const fd = new FormData();
    fd.append("files", fileToSend, item.file.name);
    try {
      const { data } = await api.post(`/gallery/events/${eventId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          updateItem(item.id, { progress: Math.round((evt.loaded / evt.total) * 100) });
        },
      });
      if (data.uploaded && data.uploaded.length > 0) {
        updateItem(item.id, { status: "uploaded", progress: 100, error: "" });
        return { outcome: "uploaded", retryable: false };
      }
      const err = (data.errors || [])[0] || { error: "Unknown error" };
      const kind = classifyErrorText(err.error);
      updateItem(item.id, {
        status: kind,
        error: kind === "duplicate" ? "" : err.error,
        progress: 100,
      });
      // Server-side errors surfaced in the `errors` array are typically
      // permanent (validation, unsupported type). Don't burn retries on them.
      return { outcome: kind, retryable: false };
    } catch (e) {
      const status = e?.response?.status;
      const detail = formatApiError(e?.response?.data?.detail) || e.message || "Upload failed";
      updateItem(item.id, { status: "failed", error: detail, progress: 0 });
      // Retryable = network error (no response) OR 5xx / 408 / 429.
      const retryable = !status || status >= 500 || status === 408 || status === 429;
      return { outcome: "failed", retryable };
    }
  }, [eventId, updateItem, uploadPreset]);

  // ---- Concurrent worker pool ----
  // `activeCountRef` tracks how many workers are in-flight so we know when
  // the whole batch is truly finished. `busy` is a UI-only mirror.
  const activeCountRef = useRef(0);

  const processOne = useCallback(async (id) => {
    let current = itemsRef.current.find((p) => p.id === id);
    if (!current) return;
    if (["uploaded", "duplicate"].includes(current.status)) return;

    let { outcome, retryable } = await uploadOne(current);

    while (outcome === "failed" && retryable && (current.retries || 0) < MAX_AUTO_RETRIES) {
      const nextRetry = (current.retries || 0) + 1;
      updateItem(current.id, { retries: nextRetry, status: "retrying", error: "" });
      // Exponential-ish backoff so a flaky network gets a chance to settle.
      await new Promise((res) => setTimeout(res, RETRY_BACKOFF_MS * nextRetry));
      current = { ...current, retries: nextRetry };
      const res = await uploadOne(current);
      outcome = res.outcome;
      retryable = res.retryable;
    }
  }, [uploadOne, updateItem]);

  const pumpQueue = useCallback(() => {
    // Fill every available slot up to the concurrency cap. Each `processOne`
    // is fire-and-forget; on completion it re-enters `pumpQueue` to grab the
    // next id, so the pool refills as soon as any slot drains.
    while (
      activeCountRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const nextId = queueRef.current.shift();
      activeCountRef.current += 1;
      setBusy(true);
      // Kick off — do NOT await, we want parallelism.
      processOne(nextId).finally(() => {
        activeCountRef.current -= 1;
        if (activeCountRef.current === 0 && queueRef.current.length === 0) {
          setBusy(false);
          onBatchDone?.();
        } else {
          // A slot just freed — try to start the next queued file.
          pumpQueue();
        }
      });
    }
  }, [onBatchDone, processOne]);

  const retryItem = useCallback((id) => {
    const idx = itemsRef.current.findIndex((p) => p.id === id);
    if (idx < 0) return;
    const row = itemsRef.current[idx];
    if (["uploaded", "duplicate"].includes(row.status)) return;
    itemsRef.current = patchItem(itemsRef.current, id, {
      status: "waiting", progress: 0, error: "", retries: 0,
    });
    setItems(itemsRef.current);
    queueRef.current.push(id);
    pumpQueue();
  }, [pumpQueue]);

  // ---- Derived stats ----
  const stats = useMemo(() => {
    let uploaded = 0, duplicate = 0, failed = 0, active = 0;
    for (const it of items) {
      if (it.status === "uploaded") uploaded += 1;
      else if (it.status === "duplicate") duplicate += 1;
      else if (it.status === "failed") failed += 1;
      else active += 1;
    }
    const done = uploaded + duplicate + failed;
    return {
      total: items.length,
      uploaded,
      duplicate,
      failed,
      active,
      done,
      allDone: items.length > 0 && done === items.length && !busy,
    };
  }, [items, busy]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      data-testid="upload-queue-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <header className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg">
              {stats.allDone ? "Upload finished" : "Uploading photos…"}
            </h3>
            <p className="text-xs text-stone-500">
              {stats.total} selected · <span className="text-emerald-700">{stats.uploaded} uploaded</span>
              {stats.duplicate > 0 && <> · <span className="text-amber-700">{stats.duplicate} duplicate</span></>}
              {stats.failed > 0 && <> · <span className="text-rose-700">{stats.failed} failed</span></>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 hover:bg-stone-100 rounded disabled:opacity-40 disabled:cursor-not-allowed"
            title={busy ? "Wait until uploads finish" : "Close"}
            data-testid="upload-queue-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-2 bg-stone-50 border-b border-stone-200">
          <div className="w-full bg-stone-200 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full bg-emerald-600 transition-all"
              style={{ width: stats.total ? `${Math.round((stats.done / stats.total) * 100)}%` : "0%" }}
              data-testid="upload-queue-overall-bar"
            />
          </div>
          <div className="text-[11px] text-stone-600 mt-1 font-mono" data-testid="upload-queue-overall-text">
            {stats.done} / {stats.total} handled
            {stats.active > 0 && ` · ${stats.active} in queue`}
          </div>
        </div>

        <ul className="flex-1 overflow-y-auto" data-testid="upload-queue-list">
          {items.map((item) => (
            <QueueRow key={item.id} item={item} onRetry={retryItem} />
          ))}
        </ul>

        <footer className="px-4 py-3 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500">
          <span>
            AI face indexing continues in the background — closing this after upload is safe.
          </span>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn btn-primary btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="upload-queue-done"
          >
            {stats.allDone ? "Done" : "Uploading…"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
